import { SIGNSTORE_H5_UID_KEY, ensureSignstoreH5Uid } from '@/utils/signstore-h5-uid'

/** 与 token 绑定的鉴权种类：anonymous | thirdPart */
export const SIGNSTORE_H5_AUTH_KIND_KEY = 'signstore_h5_auth_kind'
/** H5 用户信息缓存（静默登录返回） */
export const SIGNSTORE_H5_USER_INFO_KEY = 'signstore_h5_user_info'
/** 绑定的主体：匿名为 h5Uuid，第三方为 url 上的 uid */
export const SIGNSTORE_H5_AUTH_SUB_KEY = 'signstore_h5_auth_sub'

/** 强制走匿名 H5、忽略 URL 上 uid 的活动编码（外链误带 uid 时使用） */
export const SIGNSTORE_IGNORE_URL_UID_ACTIVITY_CODES = Object.freeze([
  'ad1f0fe16edd418f'
])

/** 该活动是否忽略链接中的 uid，不走第三方静默登录 */
export function shouldIgnoreUrlUidForActivity(activityCode) {
  const code = activityCode != null ? String(activityCode).trim() : ''
  if (!code) return false
  return SIGNSTORE_IGNORE_URL_UID_ACTIVITY_CODES.includes(code)
}

/** 解析有效第三方 uid；特定活动返回空字符串 */
export function resolveActivityThirdPartUid(activityCode, uidRaw) {
  const uid = uidRaw != null ? String(uidRaw).trim() : ''
  if (!uid || shouldIgnoreUrlUidForActivity(activityCode)) {
    return ''
  }
  return uid
}

function parseUrlSearchParams(search) {
  const out = {}
  if (!search || typeof search !== 'string') return out
  const s = search.startsWith('?') ? search.slice(1) : search
  if (!s) return out
  try {
    const sp = new URLSearchParams(s)
    sp.forEach((v, k) => {
      if (k && out[k] == null) out[k] = v
    })
  } catch (e) {
    // ignore
  }
  return out
}

/**
 * 合并活动入口 query：vue-router hash query、# 前 location.search、hash 内嵌 query。
 * 微信扫码常把 activity_code / uid 放在 # 之前，仅读 $route.query 会漏参或顺序不一致。
 */
export function mergeActivityEntryQuery(route) {
  const merged = {}
  if (typeof window !== 'undefined') {
    Object.assign(merged, parseUrlSearchParams(window.location.search))
    const hash = window.location.hash || ''
    const hashQ = hash.indexOf('?')
    if (hashQ >= 0) {
      Object.assign(merged, parseUrlSearchParams(hash.slice(hashQ)))
    }
  }
  const rq = (route && route.query) || {}
  Object.keys(rq).forEach((k) => {
    if (rq[k] != null && rq[k] !== '') merged[k] = rq[k]
  })
  return merged
}

/** 白名单活动：从浏览器 URL 与路由 query 中移除误带的 uid，避免微信历史/缓存继续走 thirdPart */
export function stripIgnoredUidFromBrowserUrl(activityCode, router) {
  if (!shouldIgnoreUrlUidForActivity(activityCode)) return false
  if (typeof window === 'undefined') return false
  let changed = false

  try {
    const sp = new URLSearchParams(window.location.search || '')
    if (sp.has('uid') || sp.has('thirdPartUid')) {
      sp.delete('uid')
      sp.delete('thirdPartUid')
      const newSearch = sp.toString()
      const hash = window.location.hash || ''
      window.history.replaceState(
        null,
        '',
        window.location.pathname + (newSearch ? `?${newSearch}` : '') + hash
      )
      changed = true
    }
  } catch (e) {
    // ignore
  }

  try {
    const hash = window.location.hash || ''
    const hashQIdx = hash.indexOf('?')
    if (hashQIdx >= 0) {
      const pathPart = hash.slice(0, hashQIdx)
      const sp = new URLSearchParams(hash.slice(hashQIdx + 1))
      if (sp.has('uid') || sp.has('thirdPartUid')) {
        sp.delete('uid')
        sp.delete('thirdPartUid')
        const newHashQs = sp.toString()
        window.history.replaceState(
          null,
          '',
          window.location.pathname +
            window.location.search +
            pathPart +
            (newHashQs ? `?${newHashQs}` : '')
        )
        changed = true
      }
    }
  } catch (e) {
    // ignore
  }

  if (router && router.currentRoute) {
    const q = Object.assign({}, router.currentRoute.query || {})
    if (q.uid || q.thirdPartUid) {
      delete q.uid
      delete q.thirdPartUid
      router.replace({ path: router.currentRoute.path, query: q }).catch(() => {})
      changed = true
    }
  }
  return changed
}

/** 后端拒绝第三方 uid（白名单活动误带 uid / 旧版 H5 仍调 thirdPart） */
export function isThirdPartUidLoginRejectedResponse(res) {
  if (!res || typeof res !== 'object') return false
  const msg = String(res.msg || res.message || '').trim()
  return msg.includes('不支持第三方uid')
}

/**
 * 活动进页鉴权：白名单活动强制匿名；thirdPart 被拒时自动回退匿名（兼容微信缓存旧 JS + 误带 uid）
 */
export async function signstoreActivityEntryAuth(opts) {
  const {
    activityCode,
    uidRaw,
    h5UidFromQuery,
    thirdPartLoginFn,
    anonymousLoginFn,
    freshAnonymous = true
  } = opts || {}
  const code = activityCode != null ? String(activityCode).trim() : ''
  const uid = resolveActivityThirdPartUid(code, uidRaw)
  const runAnonymous = async () => {
    if (freshAnonymous) {
      return signstoreAnonymousFreshLoginForActivityEntry(h5UidFromQuery, anonymousLoginFn)
    }
    return ensureSignstoreAnonymousLoggedIn(h5UidFromQuery, anonymousLoginFn)
  }
  if (!uid) {
    const auth = await runAnonymous()
    return { mode: 'anonymous', ...auth }
  }
  clearIncompatibleSignstoreAuthForThirdPart(uid)
  if (canReuseSignstoreThirdPartToken(uid)) {
    return { mode: 'thirdPart', uid, loginRes: null, reused: true }
  }
  if (typeof thirdPartLoginFn !== 'function') {
    throw new Error('第三方登录不可用')
  }
  const res = await thirdPartLoginFn({ uid, activityCode: code })
  const ok =
    res &&
    Number(res.status) === 200 &&
    res.data &&
    res.data.token &&
    !isSignstoreUnauthorizedResponse(res)
  if (ok) {
    return { mode: 'thirdPart', uid, loginRes: res }
  }
  if (shouldIgnoreUrlUidForActivity(code) || isThirdPartUidLoginRejectedResponse(res)) {
    clearSignstoreH5AuthSession()
    clearSignstoreH5UserInfo()
    const auth = await runAnonymous()
    return { mode: 'anonymous', ...auth, fallbackFromThirdPart: true }
  }
  return { mode: 'thirdPart', uid, loginRes: res, failed: true }
}

export function getSignstoreH5Token() {
  if (typeof window === 'undefined' || !window.localStorage) return ''
  try {
    const t = window.localStorage.getItem('token')
    const s = t != null ? String(t).trim() : ''
    return s && s !== 'null' ? s : ''
  } catch (e) {
    return ''
  }
}

export function markSignstoreH5Auth(kind, subject) {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.setItem(SIGNSTORE_H5_AUTH_KIND_KEY, String(kind || '').trim())
    window.localStorage.setItem(SIGNSTORE_H5_AUTH_SUB_KEY, String(subject || '').trim())
  } catch (e) {
    // ignore
  }
}

/**
 * 匿名链路是否可复用已有 token（同 h5Uuid，避免每次进页重新 silentLogin）
 */
export function canReuseSignstoreAnonymousToken(h5Uuid) {
  const h5 = h5Uuid != null ? String(h5Uuid).trim() : ''
  if (!h5) return false
  const token = getSignstoreH5Token()
  if (!token) return false

  let kind = ''
  let sub = ''
  try {
    kind = window.localStorage.getItem(SIGNSTORE_H5_AUTH_KIND_KEY) || ''
    sub = window.localStorage.getItem(SIGNSTORE_H5_AUTH_SUB_KEY) || ''
  } catch (e) {
    kind = ''
    sub = ''
  }
  kind = String(kind).trim()
  sub = String(sub).trim()

  if (kind === 'anonymous' && sub === h5) return true

  // 兼容旧版：仅有 token + signstore_h5_uid，无 auth 标记时视为同一匿名用户
  if (!kind && !sub) {
    try {
      const storedH5 = window.localStorage.getItem(SIGNSTORE_H5_UID_KEY)
      if (storedH5 != null && String(storedH5).trim() === h5) {
        markSignstoreH5Auth('anonymous', h5)
        return true
      }
    } catch (e) {
      // ignore
    }
  }
  return false
}

export function canReuseSignstoreThirdPartToken(thirdPartUid) {
  const uid = thirdPartUid != null ? String(thirdPartUid).trim() : ''
  if (!uid) return false
  const token = getSignstoreH5Token()
  if (!token) return false
  try {
    const kind = String(window.localStorage.getItem(SIGNSTORE_H5_AUTH_KIND_KEY) || '').trim()
    const sub = String(window.localStorage.getItem(SIGNSTORE_H5_AUTH_SUB_KEY) || '').trim()
    return kind === 'thirdPart' && sub === uid
  } catch (e) {
    return false
  }
}

/** 仅清除 token 与鉴权绑定（保留 signstore_h5_uid、表单 profile 等） */
export function clearSignstoreH5AuthSession() {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.removeItem('token')
    window.localStorage.removeItem(SIGNSTORE_H5_AUTH_KIND_KEY)
    window.localStorage.removeItem(SIGNSTORE_H5_AUTH_SUB_KEY)
  } catch (e) {
    // ignore
  }
}

/**
 * token 存在且 h5_uid 一致，但 auth 标记缺失/不一致时补标（避免刷新后被当成新用户）
 */
export function repairSignstoreAnonymousAuthBinding(h5Uuid) {
  const h5 = h5Uuid != null ? String(h5Uuid).trim() : ''
  if (!h5 || !getSignstoreH5Token()) return false
  if (typeof window === 'undefined' || !window.localStorage) return false
  try {
    const storedH5 = String(window.localStorage.getItem(SIGNSTORE_H5_UID_KEY) || '').trim()
    if (!storedH5 || storedH5 !== h5) return false
    const kind = String(window.localStorage.getItem(SIGNSTORE_H5_AUTH_KIND_KEY) || '').trim()
    if (kind && kind !== 'anonymous') return false
    markSignstoreH5Auth('anonymous', h5)
    return true
  } catch (e) {
    return false
  }
}

/** 当前匿名用户无法复用 token 时清理（如残留第三方登录 token） */
export function clearIncompatibleSignstoreAuthForAnonymous(h5Uuid) {
  if (canReuseSignstoreAnonymousToken(h5Uuid)) return false
  if (repairSignstoreAnonymousAuthBinding(h5Uuid)) return false
  if (!getSignstoreH5Token()) return false
  clearSignstoreH5AuthSession()
  return true
}

export function clearIncompatibleSignstoreAuthForThirdPart(thirdPartUid) {
  if (canReuseSignstoreThirdPartToken(thirdPartUid)) return false
  clearSignstoreH5AuthSession()
  return true
}

export function getSignstoreUnauthorizedMessage(res) {
  if (!res || typeof res !== 'object') return ''
  return String(res.msg || res.message || '').trim()
}

/** 后端 ApiCode.UNAUTHORIZED(401, "非法访问") 及同类未授权响应 */
export function isSignstoreUnauthorizedResponse(res) {
  if (!res || typeof res !== 'object') return false
  const status = Number(res.status)
  if (status === 401) return true
  const msg = getSignstoreUnauthorizedMessage(res)
  if (!msg) return false
  return (
    msg === '非法访问' ||
    msg.includes('非法访问') ||
    msg.includes('未授权') ||
    msg.includes('未登录') ||
    msg.toLowerCase().includes('unauthorized')
  )
}

/** token 中 uid 已在库中删除，需清缓存后重新静默登录 */
export function isSignstoreStaleUserError(resOrErr) {
  let msg = ''
  if (typeof resOrErr === 'string') {
    msg = resOrErr
  } else if (resOrErr && typeof resOrErr === 'object') {
    if (resOrErr.msg != null) msg = String(resOrErr.msg)
    else if (resOrErr.message != null) msg = String(resOrErr.message)
    else if (resOrErr.response && resOrErr.response.data && resOrErr.response.data.msg != null) {
      msg = String(resOrErr.response.data.msg)
    }
  }
  return /用户不存在/.test(msg)
}

export function clearSignstoreH5UserInfo() {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.removeItem(SIGNSTORE_H5_USER_INFO_KEY)
  } catch (e) {
    // ignore
  }
}

/** 清除匿名身份相关缓存（token、鉴权标记、h5_uid、用户信息） */
export function clearSignstoreH5AnonymousIdentity() {
  clearSignstoreH5AuthSession()
  clearSignstoreH5UserInfo()
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.removeItem(SIGNSTORE_H5_UID_KEY)
  } catch (e) {
    // ignore
  }
}

/** 视为新匿名用户：清缓存并生成新的 h5_uid */
export function resetSignstoreH5AnonymousAsNewUser() {
  clearSignstoreH5AnonymousIdentity()
  return ensureSignstoreH5Uid()
}

export function persistSignstoreH5LoginPayload(payload, kind, subject) {
  if (!payload || typeof window === 'undefined' || !window.localStorage) return
  try {
    const token = payload.token
    if (token) {
      window.localStorage.setItem('token', token)
    }
    const userInfo = payload.userInfo
    if (userInfo) {
      window.localStorage.setItem(SIGNSTORE_H5_USER_INFO_KEY, JSON.stringify(userInfo))
    }
    if (kind && subject != null && String(subject).trim() !== '') {
      markSignstoreH5Auth(kind, subject)
    }
  } catch (e) {
    // ignore
  }
}

/** 进活动页：清除旧 token / 用户信息（保留 h5_uid，避免旧商城 token 干扰） */
export function clearSignstoreTokenForActivityEntry() {
  clearSignstoreH5AuthSession()
  clearSignstoreH5UserInfo()
}

/**
 * 活动进页：清旧 token 后强制 H5 匿名 silentLogin（不复用缓存 token）
 * @param {string} [fromQuery] 路由 h5_uid
 * @param {(body: { h5Uuid: string }) => Promise<any>} silentLoginFn
 */
export async function signstoreAnonymousFreshLoginForActivityEntry(fromQuery, silentLoginFn) {
  const h5Uid = ensureSignstoreH5Uid(fromQuery)
  clearSignstoreTokenForActivityEntry()

  const runLogin = async (uuid) => {
    if (typeof silentLoginFn !== 'function') {
      throw new Error('匿名登录不可用')
    }
    return silentLoginFn({ h5Uuid: uuid })
  }

  const accept = (res) =>
    res && Number(res.status) === 200 && res.data && res.data.token && !isSignstoreUnauthorizedResponse(res)

  let res = await runLogin(h5Uid)
  if (accept(res)) {
    persistSignstoreH5LoginPayload(res.data, 'anonymous', h5Uid)
    return { h5Uid, loginRes: res, rotated: false }
  }

  if (isSignstoreUnauthorizedResponse(res)) {
    const retryRes = await runLogin(h5Uid)
    if (accept(retryRes)) {
      persistSignstoreH5LoginPayload(retryRes.data, 'anonymous', h5Uid)
      return { h5Uid, loginRes: retryRes, rotated: false, recovered: true }
    }
    res = retryRes
  }

  const newH5Uid = resetSignstoreH5AnonymousAsNewUser()
  const rotatedRes = await runLogin(newH5Uid)
  if (!accept(rotatedRes)) {
    const msg = getSignstoreUnauthorizedMessage(rotatedRes) || (rotatedRes && rotatedRes.msg) || '匿名登录失败'
    throw new Error(msg)
  }
  persistSignstoreH5LoginPayload(rotatedRes.data, 'anonymous', newH5Uid)
  return { h5Uid: newH5Uid, loginRes: rotatedRes, rotated: true }
}

/**
 * 匿名 H5 鉴权：优先复用浏览器缓存；无 token 再 silentLogin；
 * 非法访问则清 token 后用同一 h5_uid 重登；仍失败才换新 h5_uid。
 * @param {string} [fromQuery] 路由 h5_uid
 * @param {(body: { h5Uuid: string }) => Promise<any>} silentLoginFn
 */
export async function ensureSignstoreAnonymousLoggedIn(fromQuery, silentLoginFn) {
  const h5Uid = ensureSignstoreH5Uid(fromQuery)
  clearIncompatibleSignstoreAuthForAnonymous(h5Uid)

  if (canReuseSignstoreAnonymousToken(h5Uid)) {
    return { h5Uid, reused: true, rotated: false, loginRes: null }
  }

  const runLogin = async (uuid) => {
    if (typeof silentLoginFn !== 'function') {
      throw new Error('匿名登录不可用')
    }
    return silentLoginFn({ h5Uuid: uuid })
  }

  const accept = (res) =>
    res && Number(res.status) === 200 && res.data && res.data.token && !isSignstoreUnauthorizedResponse(res)

  let res = await runLogin(h5Uid)
  if (accept(res)) {
    persistSignstoreH5LoginPayload(res.data, 'anonymous', h5Uid)
    return { h5Uid, reused: false, rotated: false, loginRes: res }
  }

  if (isSignstoreUnauthorizedResponse(res)) {
    clearSignstoreH5AuthSession()
    clearSignstoreH5UserInfo()
    const retryRes = await runLogin(h5Uid)
    if (accept(retryRes)) {
      persistSignstoreH5LoginPayload(retryRes.data, 'anonymous', h5Uid)
      return { h5Uid, reused: false, rotated: false, recovered: true, loginRes: retryRes }
    }
    res = retryRes
  }

  const newH5Uid = resetSignstoreH5AnonymousAsNewUser()
  const rotatedRes = await runLogin(newH5Uid)
  if (!accept(rotatedRes)) {
    const msg = getSignstoreUnauthorizedMessage(rotatedRes) || (rotatedRes && rotatedRes.msg) || '匿名登录失败'
    throw new Error(msg)
  }
  persistSignstoreH5LoginPayload(rotatedRes.data, 'anonymous', newH5Uid)
  return { h5Uid: newH5Uid, reused: false, rotated: true, loginRes: rotatedRes }
}

/**
 * 构造活动跳转页路由（匿名新用户重进 / 非法访问恢复）
 * @param {import('vue-router').default | { currentRoute?: { query?: object } }} router
 */
export function buildSignstoreActivityRedirectRoute(router, opts = {}) {
  const current =
    (router && router.currentRoute && router.currentRoute.query) ||
    (opts && opts.query) ||
    {}
  const query = Object.assign({}, current, opts.extraQuery || {})
  const code =
    (opts.activityCode != null ? String(opts.activityCode).trim() : '') ||
    (query.activity_code != null ? String(query.activity_code).trim() : '') ||
    (query.activityCode != null ? String(query.activityCode).trim() : '')
  if (code) {
    query.activity_code = code
    delete query.activityCode
    if (shouldIgnoreUrlUidForActivity(code)) {
      delete query.uid
      delete query.thirdPartUid
    }
  }
  if (opts.pageKey != null && String(opts.pageKey).trim() !== '') {
    query.pageKey = String(opts.pageKey).trim()
  }
  if (opts.h5Uid != null && String(opts.h5Uid).trim() !== '') {
    query.h5_uid = String(opts.h5Uid).trim()
    delete query.uid
  }
  if (opts.id != null && String(opts.id).trim() !== '') {
    query.id = String(opts.id).trim()
  }
  return { path: '/loading/activityRedirect', query }
}
